import { SB } from '@play-co/replicant';
import {
  duoAssets,
  duoInviteMessage,
  duoReminderMessage,
  duoResponseMessage,
} from './duo';
import { fullenergyMessage, fullenergyAssets } from './fullenergy';
import { joinedAssets, joinedMessage } from './joined';
import { SpeciesChatbotType, speciesAssets } from './species';
import { stateAssets, statusMessage } from './status';
import { visitedAssets, visitedMessage } from './visited';
import { daycareAssets, daycareMessage } from './daycare';
import {
  reengagementAssets,
  reengagementMessage,
  reengagementVideoMessage,
} from './reengagement';
import { giftAssets, giftMessage, giftMessageBatched } from './gift';
import { friendsAssets, friendshipRequestMessage } from './friends';
import {
  friendRequestAcceptedAssets,
  friendRequestAcceptedMessage,
} from './friendRequestAccepted';
import {
  resurrectAssets,
  resurrectExtraAssets,
  resurrectMessage,
} from './resurrect';
import { dailyBonusAssets, dailyBonusMessage } from './dailybonus';
import { spawnPetAssets, spawnPetMessage } from './spawnPet';
import { liveOpsAssets, liveOpsMessage } from './liveops';
import { revivedAssets, revivedMessage } from './revived';
import { backToLifeAssets, backToLifeMessage } from './backToLife';
import {
  cardReceivedMessage,
  cardRequestedMessage,
  cardsAssets,
} from './cards';
import { chatMessage, chatAssets } from './chat';
import { replyFlexMessage } from './reply';
import { shareFlexMessage } from './share';
import { retentionAssets, retentionMessage } from './retention';
import {
  greetingAssets,
  greetingImageMessage,
  greetingMessage,
} from './greeting';
import { miniappCreativeAssets, miniappWelcome } from './miniapp';
import { buddiesAssets, buddiesInvitation, buddiesNudge } from './buddies';
import { jankenAssets, jankenResultMessage } from './janken';
import { crateWaitingAssets, crateWaitingMessage } from './crateWaiting';
import { preEntryInviteMessage } from './preEntryInvite';
import {
  purchaseConfirmationAssets,
  purchaseConfirmationMessage,
} from './purchaseConfirmation';

/**
 * Here we collect all templates variants and assets
 */

// Static assets used for generation flex messages templates
// Please prefix assets with flex_ prefix to avoid collisions
export const chatbotFlexAssets = {
  ...fullenergyAssets,
  ...speciesAssets,
  ...stateAssets,
  ...visitedAssets,
  ...duoAssets,
  ...joinedAssets,
  ...daycareAssets,
  ...reengagementAssets,
  ...giftAssets,
  ...friendsAssets,
  ...friendRequestAcceptedAssets,
  ...resurrectAssets,
  ...resurrectExtraAssets,
  ...dailyBonusAssets,
  ...spawnPetAssets,
  ...liveOpsAssets,
  ...revivedAssets,
  ...backToLifeAssets,
  ...cardsAssets,
  ...chatAssets,
  ...retentionAssets,
  ...greetingAssets,
  ...miniappCreativeAssets,
  ...buddiesAssets,
  ...jankenAssets,
  ...crateWaitingAssets,
  ...purchaseConfirmationAssets,
  // Common assets
  'button/playnow_en': 'private/chatbot/common/button_playnow_en.png',
  'button/playnow_ja': 'private/chatbot/common/button_playnow_ja.png',
  // Add new assets here
};

/**
 * Sizes of species images for chatbot messages.
 */
export const petSizes: Record<SpeciesChatbotType, 'full' | `${number}%`> = {
  dog: 'full',
  ball: '80%',
  starball: '80%',
  crab: 'full',
  hamster: 'full',
  turtle: 'full',
  squirrel: 'full',
  seaotter: '90%',
  cat: 'full',
  whale: 'full',
  giraffe: 'full',
  dinosaur: 'full',
  fish: 'full',
  pig: 'full',
  pinkdog: 'full',
  panda: 'full',
  bear: 'full',
  penguin: 'full',
  lion: 'full',
  elephant: 'full',
  reindeer: 'full',
  polarbear: 'full',
  alpaca: 'full',
  tiger: 'full',
  sealion: 'full',
  flamingo: 'full',
  cow: 'full',
  persiancat: 'full',
  dolphin: 'full',
};

/**
 * Add new messages templates here to make them available for usage
 *
 * For sending flexible message you should call:
 * @example
 *
 * // Create template
 * const message = getChatbotFlexMessage({
 *    data: { ... data required for message ... },
 *    template: 'fullenergy', <- See demoMessages const below
 *    payload: { ...standard message payload ...)
 * });
 *
 * // Send chatbot message
 * api.chatbot.sendMessage('USER ID', message);
 */
// Please prefix messages with flex_ prefix to avoid collisions
export const chatbotFlexTemplates = {
  fullenergy: fullenergyMessage,
  status: statusMessage,
  visited: visitedMessage,
  revived: revivedMessage,
  duoInvite: duoInviteMessage,
  duoReminder: duoReminderMessage,
  duoResponse: duoResponseMessage,
  friendJoined: joinedMessage,
  daycare: daycareMessage,
  reengagement: reengagementMessage,
  reengagementVideo: reengagementVideoMessage,
  giftMessage: giftMessage,
  giftMessageBatched: giftMessageBatched,
  friendshipRequestMessage: friendshipRequestMessage,
  friendRequestAcceptedMessage: friendRequestAcceptedMessage,
  resurrect: resurrectMessage,
  dailyBonusMessage: dailyBonusMessage,
  spawnPet: spawnPetMessage,
  preEntryInvite: preEntryInviteMessage,
  liveOps: liveOpsMessage,
  backToLife: backToLifeMessage,
  cardReceived: cardReceivedMessage,
  cardRequested: cardRequestedMessage,
  chat: chatMessage,
  reply: replyFlexMessage,
  share: shareFlexMessage,
  retentionMessage: retentionMessage,
  greeting: greetingMessage,
  greetingImage: greetingImageMessage,
  miniappWelcome: miniappWelcome,
  buddiesInvitation: buddiesInvitation,
  buddiesNudge: buddiesNudge,
  jankenResult: jankenResultMessage,
  crateWaiting: crateWaitingMessage,
  purchaseConfirmation: purchaseConfirmationMessage,
  // Add new templates here
};

// Available messages templates
export type FlexMessagesTemplate = keyof typeof chatbotFlexTemplates;

// Define list of required arguments
export type FlexMessagesArgs = {
  [key in FlexMessagesTemplate]: SB.ExtractType<
    (typeof chatbotFlexTemplates)[key]['args']
  >;
};
